/**
 * Hero — two-column: the spine (headline + pre-launch CTAs) beside the
 * "money shot" — a stylized product panel showing the sworn claim number,
 * its dual-method composition, and its lineage. Shows the product instead
 * of only describing it.
 *
 * Pre-launch: primary CTA → /waitlist (early access), secondary → /demo.
 * No orbs, gradients, glass, animation, or emoji. Real fonts + tokens.
 *
 * Top padding clears the fixed site header (compact wordmark, ≈84px).
 */

import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../ui/Button';

/** The money shot: a case's sworn number with its dual-method composition,
 *  framed in app chrome, with provenance. Illustrative sample data — a demo
 *  of our own UI, not a claim about a specific client. */
const HAIR = { borderColor: 'var(--color-hairline)' } as const;
const VERIFIED = { color: 'var(--color-verified)' } as const;

/** The money shot as a court EXHIBIT (matches the seat exhibits): square, ruled,
 *  flat, tabular — no chrome dots, no pills. One orange atom: the figure. */
function CaseMoneyShot() {
  const stages = ['Intake', 'Analysis', 'Method', 'Settle', 'Deliver', 'Finalize'];
  const activeStage = 'Finalize';

  return (
    <figure className="border bg-surface" style={HAIR} aria-hidden="true">
      {/* Exhibit header */}
      <div
        className="flex items-center justify-between gap-3 border-b px-5 py-3 font-mono text-[10px] uppercase tracking-[0.16em]"
        style={HAIR}
      >
        <span className="truncate text-text-muted">Case D6D88AEA · Summit Ridge</span>
        <span className="shrink-0 text-text-dim">Illustrative sample</span>
      </div>

      {/* Matter block */}
      <div className="flex items-start justify-between gap-4 border-b px-5 py-5 sm:px-6" style={HAIR}>
        <div className="min-w-0">
          <h2 className="font-heading text-[22px] leading-tight text-text-primary">Summit Ridge Restaurant Group</h2>
          <p className="mt-1.5 font-mono text-[11px] uppercase tracking-[0.08em] text-text-muted">
            Fire · Loss Nov 14, 2025 · Dual method
          </p>
        </div>
        <p className="shrink-0 text-right font-mono text-[10px] leading-relaxed">
          <span className="block uppercase tracking-[0.12em] text-text-dim">Status</span>
          <span className="text-text-secondary">
            CPA Attested <span style={VERIFIED}>✓</span>
          </span>
        </p>
      </div>

      {/* The number — total_demand (L_PROP + L_BI) is the dual-pillar headline, a
          labeled composition (NEVER an attested blend — total_demand_attested is
          always false). The ✓ sits on the figure the product actually attests: the
          supported net claim (BI net, policy_deductions.net_claim). */}
      <div className="px-5 py-6 sm:px-6">
        <div className="flex items-baseline justify-between gap-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-text-muted">Total demand</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-text-dim">L_PROP + L_BI</span>
        </div>
        <p className="mt-2 font-mono text-5xl leading-none tracking-tight text-brand-text">$412,309</p>

        <div className="mt-5 border-t pt-3" style={HAIR}>
          <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-text-dim">Composed of</p>
          <div className="mt-1.5 flex items-baseline justify-between gap-4 border-b py-3" style={HAIR}>
            <span className="font-body text-[13px] text-text-secondary">Business interruption — net</span>
            <span className="font-mono text-sm tabular-nums text-text-primary">$155,309</span>
          </div>
          <div className="flex items-baseline justify-between gap-4 py-3">
            <span className="font-body text-[13px] text-text-secondary">First-party property — RCV</span>
            <span className="font-mono text-sm tabular-nums text-text-primary">$257,000</span>
          </div>
        </div>

        <p className="mt-4 font-mono text-[10px] uppercase leading-relaxed tracking-[0.06em] text-text-dim">
          <span style={VERIFIED}>✓</span> Attested — <span className="text-text-secondary">M. T. Ellison, CPA</span> · TX Lic #104827 · 01.08.26
        </p>
        <p className="mt-2 font-mono text-[10px] uppercase leading-relaxed tracking-[0.08em] text-text-dim">
          Traced to: P&amp;L 2024–25 · engine BI lost-profits · CP 00 30 period
        </p>
      </div>

      {/* Stage strip — active in cream (no orange: the figure is the one orange atom) */}
      <div className="border-t px-5 py-3 sm:px-6" style={HAIR}>
        <div className="flex items-center justify-between gap-1 font-mono text-[10px] uppercase tracking-[0.04em]">
          {stages.map((s) => (
            <span
              key={s}
              className={s === activeStage ? 'pb-0.5 text-text-primary' : 'text-text-dim'}
              style={s === activeStage ? { borderBottom: '2px solid var(--color-text-primary)' } : undefined}
            >
              {s}
            </span>
          ))}
        </div>
      </div>
    </figure>
  );
}

export default function Hero() {
  return (
    <section id="hero" className="px-5 pb-16 pt-32 sm:px-8 lg:px-12 lg:pb-24 lg:pt-40">
      <div className="mx-auto grid max-w-[1400px] items-center gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
        {/* Left: the spine */}
        <div className="min-w-0">
          <p className="mb-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-text">
            <span className="h-1.5 w-1.5 bg-brand" />
            Private beta · BI claims
          </p>

          <h1 className="max-w-2xl font-heading text-4xl leading-[1.08] tracking-tight text-text-primary sm:text-5xl lg:text-[3.4rem]">
            One claim. <span className="whitespace-nowrap">Engine-grounded</span> numbers no side can wave away.
          </h1>
          <p className="mt-3 max-w-2xl font-heading text-2xl leading-tight tracking-tight text-text-muted sm:text-3xl">
            And the documents to assert them.
          </p>

          <p className="mt-6 max-w-xl font-body text-lg leading-relaxed text-text-secondary">
            The dollar figure is computed by deterministic engines — not an AI
            guess — reviewed by the right human, and traceable to the document
            it came from.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-4">
            <Button
              asChild
              size="lg"
              className="!rounded-none !bg-brand px-7 font-mono text-xs font-semibold uppercase tracking-[0.14em] shadow-none hover:!bg-brand-hover"
              style={{ color: 'var(--color-on-brand)' }}
            >
              <Link to="/waitlist">
                Request early access
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="!rounded-none border-hairline bg-transparent px-6 font-mono text-xs font-semibold uppercase tracking-[0.14em] text-text-primary shadow-none hover:bg-elevated"
            >
              <a href="#architecture">
                See how it works
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
            </Button>
          </div>

          <p className="mt-6 font-mono text-xs text-text-muted">
            Built to withstand cross-examination.
          </p>
        </div>

        {/* Right: the money shot */}
        <div className="min-w-0 lg:pl-4">
          <CaseMoneyShot />
          <p
            className="mt-4 border-t pt-3 font-mono text-[10px] uppercase tracking-[0.1em] leading-relaxed text-text-dim"
            style={HAIR}
          >
            Fig. — the sworn figure and where it came from
          </p>
        </div>
      </div>
    </section>
  );
}
